// src/components/ErrorBoundary.js
import React, { Component } from 'react';
import './ErrorBoundary.css';

/**
 * ErrorBoundary - Catches JavaScript errors anywhere in their child component tree
 * and displays a fallback UI instead of crashing the whole app.
 * 
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 * 
 * Or with custom fallback:
 * <ErrorBoundary fallback={<CustomErrorComponent />}>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Store error details for display
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // TODO: Log to error reporting service (e.g., Sentry)
    // if (typeof window !== 'undefined' && window.Sentry) {
    //   window.Sentry.captureException(error, { extra: errorInfo });
    // }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI provided via props
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">⚠️</div>
            <h1 className="error-boundary-title">Something went wrong</h1>
            <p className="error-boundary-message">
              We're sorry, but something unexpected happened. Please try again.
            </p>
            
            <div className="error-boundary-actions">
              <button onClick={this.handleRetry} className="error-boundary-btn error-boundary-btn--primary">
                Try Again
              </button>
              <button onClick={this.handleReload} className="error-boundary-btn error-boundary-btn--secondary">
                Reload Page
              </button>
              <button onClick={this.handleGoHome} className="error-boundary-btn error-boundary-btn--link">
                Go to Home
              </button>
            </div>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-boundary-details">
                <summary>Error Details (Development Only)</summary>
                <div className="error-boundary-stack">
                  <strong>{this.state.error?.toString()}</strong>
                  <pre>{this.state.errorInfo?.componentStack}</pre>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * withErrorBoundary - HOC to wrap a component with ErrorBoundary
 * 
 * Usage:
 * export default withErrorBoundary(MyComponent);
 */
export function withErrorBoundary(WrappedComponent, fallback = null) {
  return function WithErrorBoundaryComponent(props) {
    return (
      <ErrorBoundary fallback={fallback}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

/**
 * PageErrorBoundary - Error boundary specifically for page-level errors
 * Shows a more user-friendly message with navigation options
 */
export class PageErrorBoundary extends ErrorBoundary {
  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary error-boundary--page">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">🎰</div>
            <h1 className="error-boundary-title">Oops! Hit a bad beat</h1>
            <p className="error-boundary-message">
              The page encountered an error. Don't worry, your data is safe.
            </p>
            
            <div className="error-boundary-actions">
              <button onClick={this.handleGoHome} className="error-boundary-btn error-boundary-btn--primary">
                🏠 Home
              </button>
              <button onClick={this.handleReload} className="error-boundary-btn error-boundary-btn--secondary">
                🔄 Reload Page
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-boundary-details">
                <summary>Technical Details</summary>
                <div className="error-boundary-stack">
                  <strong>{this.state.error?.toString()}</strong>
                  <pre>{this.state.errorInfo?.componentStack}</pre>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
